import { useEffect, useMemo, useState } from "react";
import DashboardSection from "./DashboardSection";
import DashboardKpiInfoPopover from "./DashboardKpiInfoPopover";
import { formatPilotageCurrency, formatPilotageCount, formatPilotageDecimal, getObjectiveStatus } from "@/lib/pilotageUi";
import { cn } from "@/lib/utils";
import { getExecutiveCockpit } from "@/lib/pilotageApi";

const iso = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const PRESETS = ["YTD", "FULL_YEAR", "T1", "T2", "T3", "T4", "S1", "S2", "CURRENT_MONTH", "PREVIOUS_MONTH", "CUSTOM"];
const LABELS = { YTD: "Année à date", FULL_YEAR: "Année complète", CURRENT_MONTH: "Mois courant", PREVIOUS_MONTH: "Mois précédent", CUSTOM: "Personnalisé" };
export const resolvePeriod = (preset, year, customStart, customEnd) => {
  const now = new Date();
  if (preset === "CUSTOM" && customStart && customEnd) return [customStart, customEnd];
  if (preset === "YTD") return [`${year}-01-01`, year === now.getFullYear() ? iso(now) : `${year}-12-31`];
  if (preset === "FULL_YEAR") return [`${year}-01-01`, `${year}-12-31`];
  if (/^T[1-4]$/.test(preset)) {
    const startMonth = (Number(preset[1]) - 1) * 3;
    return [iso(new Date(year, startMonth, 1)), iso(new Date(year, startMonth + 3, 0))];
  }
  if (preset === "S1") return [`${year}-01-01`, `${year}-06-30`];
  if (preset === "S2") return [`${year}-07-01`, `${year}-12-31`];
  const monthOffset = preset === "PREVIOUS_MONTH" ? -1 : 0;
  const monthDate = new Date(year, now.getMonth() + monthOffset, 1);
  const end = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
  return [iso(monthDate), iso(end)];
};

const ExecutiveCard = ({
  label,
  target,
  actual,
  delta,
  percent,
  type = "currency",
  infoNode,
  dataTestId,
  valueClassName,
  subtext,
  realizedValue,
  forecastValue,
  mixed = false,
  showObjectives = false,
}) => {
  const isPositiveDelta = delta > 0;
  const isNegativeDelta = delta < 0;
  
  // Inverse color logic for fixed costs
  const isCost = label.includes("Charges fixes");
  const isGood = isCost ? isNegativeDelta : isPositiveDelta;
  const isBad = isCost ? isPositiveDelta : isNegativeDelta;
  
  const colorClass = isGood ? "text-emerald-400" : isBad ? "text-rose-400" : "text-slate-400";
  const sign = isPositiveDelta ? "+" : "";
  
  const formatValue = (val) => {
    if (val === null || val === undefined) return "—";
    if (type === "currency") return formatPilotageCurrency(val);
    if (type === "count") return formatPilotageCount(val);
    if (type === "decimal") return formatPilotageDecimal(val);
    if (type === "percent") return `${Math.round(val)} %`;
    return val;
  };

  const { status, label: progressionLabel, progress } = getObjectiveStatus(actual, target);

  let progressionColorClass = "bg-slate-500/10 text-slate-400";
  let progressionBarColorClass = "bg-slate-400";
  
  if (status === "exceeded" || status === "achieved") {
    progressionColorClass = "bg-emerald-500/10 text-emerald-400";
    progressionBarColorClass = "bg-emerald-500";
  } else {
    // status === "in_progress"
    const isNegativeTarget = typeof target === "number" && target < 0;
    if (isNegativeTarget) {
      progressionColorClass = "bg-rose-500/10 text-rose-400";
      progressionBarColorClass = "bg-rose-500";
    } else if (percent !== null && percent !== undefined) {
      if (percent >= 90 && percent <= 100) {
        progressionColorClass = "bg-amber-500/10 text-amber-400";
        progressionBarColorClass = "bg-amber-500";
      } else if (percent < 90) {
        progressionColorClass = isCost ? "bg-emerald-500/10 text-emerald-400" : "bg-rose-500/10 text-rose-400";
        progressionBarColorClass = isCost ? "bg-emerald-500" : "bg-rose-500";
      }
    }
  }

  const isNegativeTarget = typeof target === "number" && target < 0;
  const showProgression = showObjectives && ((percent !== null && percent !== undefined) || isNegativeTarget);
  const hasTarget = showObjectives && target !== null && target !== undefined;
  const hasDelta = showObjectives && delta !== null && delta !== undefined;
  const hasObjectives = hasTarget || hasDelta || showProgression;
  const hasBottomContent = mixed || Boolean(subtext) || hasObjectives;

  return (
    <div className="flex h-full min-w-0 flex-col rounded-xl border border-white/10 bg-white/[0.04] p-5 shadow-sm shadow-black/20 relative" data-testid={dataTestId}>
      <div className="flex min-w-0 items-start justify-between gap-3 mb-4">
        <p className="min-w-0 flex-1 break-words text-xs font-semibold uppercase tracking-[0.12em] leading-relaxed text-slate-400" data-testid={dataTestId ? `${dataTestId}-label` : undefined}>
          {label}
        </p>
        {infoNode && (
          <div className="flex flex-none items-center gap-1">
            {infoNode}
          </div>
        )}
      </div>

      <div className={cn("text-4xl font-semibold text-white tracking-tight", hasBottomContent ? "mb-6" : "mb-0", valueClassName)} data-testid={dataTestId ? `${dataTestId}-value` : undefined}>
        {formatValue(actual)}
      </div>

      {hasBottomContent && (
        <div className="mt-auto flex flex-col gap-3">
          {mixed ? (
            <div
              className={cn("space-y-2 text-xs", (hasObjectives || subtext) && "border-b border-white/5 pb-3")}
              data-testid={dataTestId ? `${dataTestId}-breakdown` : undefined}
            >
              <div className="flex min-w-0 items-center justify-between gap-3 text-emerald-300">
                <span className="shrink-0"><span aria-hidden="true">●</span> Réalisé</span>
                <span className="min-w-0 break-words text-right font-semibold tabular-nums">{formatValue(realizedValue)}</span>
              </div>
              <div className="flex min-w-0 items-center justify-between gap-3 text-amber-300">
                <span className="shrink-0"><span aria-hidden="true">○</span> Estimatif</span>
                <span className="min-w-0 break-words text-right font-semibold tabular-nums">{formatValue(forecastValue)}</span>
              </div>
            </div>
          ) : null}
          {subtext && (
            <p className={cn("text-sm leading-relaxed text-slate-400", hasObjectives && "border-b border-white/5 pb-3")}>
              {subtext}
            </p>
          )}

          {hasTarget && (
            <div className="flex justify-between items-center text-sm text-slate-400 border-b border-white/5 pb-2" data-testid={dataTestId ? `${dataTestId}-target-row` : undefined}>
              <span>Objectif :</span>
              <span className="text-white font-medium" data-testid={dataTestId ? `${dataTestId}-target` : undefined}>{formatValue(target)}</span>
            </div>
          )}

          {hasDelta && (
            <div className="flex justify-between items-center text-sm border-b border-white/5 pb-2" data-testid={dataTestId ? `${dataTestId}-delta-row` : undefined}>
              <span className="text-slate-400">Écart :</span>
              <span className={cn("font-medium", colorClass)} data-testid={dataTestId ? `${dataTestId}-delta` : undefined}>
                {sign}{formatValue(delta)}
              </span>
            </div>
          )}

          {showProgression && (
            <div className="flex flex-col gap-2 pt-1" data-testid={dataTestId ? `${dataTestId}-progression-container` : undefined}>
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-400">Progression :</span>
                <div
                  className={cn("px-2.5 py-0.5 rounded-md font-medium text-xs border border-current/10", progressionColorClass)}
                  data-testid={dataTestId ? `${dataTestId}-progression-badge` : undefined}
                >
                  {progressionLabel}
                </div>
              </div>
              <div
                className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden mt-1"
                role="progressbar"
                aria-label={`Progression de l'objectif ${label}`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.max(0, Math.min(100, Number(progress) || 0))}
              >
                <div
                  className={cn(
                    "h-full rounded-full transition-[width] duration-500",
                    progressionBarColorClass
                  )}
                  style={{ width: `${progress}%` }}
                  data-testid={dataTestId ? `${dataTestId}-progression-bar` : undefined}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CeoExecutiveKpis = ({ comparisons = {}, finance = {}, activeResourcesCount, objectiveId, globalYear }) => {
  const [searchParams, setLocalSearchParams] = useState(
    () => new URLSearchParams(typeof window === "undefined" ? "" : window.location.search),
  );
  const currentYear = new Date().getFullYear();
  const rawPeriod = searchParams.get("period");
  const preset = PRESETS.includes(rawPeriod) ? rawPeriod : "FULL_YEAR";
  const showObjectives = rawPeriod ? rawPeriod === "FULL_YEAR" : preset === "FULL_YEAR";
  const requestedYear = Number(searchParams.get("executive_year") || globalYear || searchParams.get("year"));
  const year = [2026, 2027].includes(requestedYear) ? requestedYear : Math.min(2027, Math.max(2026, currentYear));
  const customStart = searchParams.get("start") || `${year}-01-01`;
  const customEnd = searchParams.get("end") || `${year}-12-31`;
  const [startDate, endDate] = useMemo(
    () => resolvePeriod(preset, year, customStart, customEnd),
    [preset, year, customStart, customEnd],
  );
  const [executive, setExecutive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setExecutive(null);
    if (typeof getExecutiveCockpit !== "function") { setLoading(false); return undefined; }
    getExecutiveCockpit({
      start_date: startDate,
      end_date: endDate,
      objective_id: (!globalYear || year === globalYear) ? objectiveId || undefined : undefined,
    })
      .then((payload) => { if (!cancelled) setExecutive(payload); })
      .catch(() => { if (!cancelled) setError("Impossible de recalculer cette période."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [startDate, endDate, objectiveId, globalYear, year]);

  const updateQuery = (changes) => {
    const next = new URLSearchParams(searchParams);
    if (changes.executive_year) {
      next.delete("start");
      next.delete("end");
    }
    Object.entries(changes).forEach(([key, value]) => next.set(key, String(value)));
    setLocalSearchParams(next);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `${window.location.pathname}?${next.toString()}${window.location.hash}`);

    }
  };
  const actualSection = executive?.actual?.available ? executive.actual : null;
  const forecastSection = executive?.forecast?.available ? executive.forecast : null;
  const isMixed = Boolean(actualSection && forecastSection && executive?.landing?.available);
  const primarySection = isMixed ? executive.landing : (actualSection || forecastSection);
  const primaryComparisons = primarySection?.comparisons;
  const sameGlobalYear = !globalYear || year === globalYear;
  const canUseLegacyFallback = typeof getExecutiveCockpit !== "function";
  const safeComp = (key, legacyKey = key) => primaryComparisons?.[key] || (sameGlobalYear && canUseLegacyFallback ? comparisons[legacyKey] : null) || {};
  const sectionValue = (section, key) => section?.comparisons?.[key]?.actual;
  const requestedPeriod = executive?.requested_period;
  const shownStart = requestedPeriod?.start_date || startDate;
  const shownEnd = requestedPeriod?.end_date || endDate;

  const staffing = primarySection?.operations || {};
  const hasAssignedCount = Number.isFinite(staffing.assigned_resources);
  const cards = [
    {
      label: actualSection && !forecastSection ? "Chiffre d'affaires réalisé" : forecastSection && !actualSection ? "Chiffre d'affaires estimé" : "Chiffre d'affaires",
      ...safeComp("revenue", "ca_annual_net"),
      realizedValue: sectionValue(actualSection, "revenue"),
      forecastValue: sectionValue(forecastSection, "revenue"),
      type: "currency",
      infoNode: <DashboardKpiInfoPopover kpiKey="ca_annuel_net" />,
      dataTestId: "pilotage-dashboard-kpi-ceo-ca",
    },
    {
      label: forecastSection && !actualSection ? "Résultat net estimé" : "Résultat net",
      ...safeComp("net_result", "forecast_result"),
      realizedValue: sectionValue(actualSection, "net_result"),
      forecastValue: sectionValue(forecastSection, "net_result"),
      type: "currency",
      infoNode: <DashboardKpiInfoPopover kpiKey="resultat_net_annuel" />,
      dataTestId: "pilotage-dashboard-kpi-ceo-net-profit",
    },
    {
      label: forecastSection && !actualSection ? "Charges fixes estimées" : "Charges fixes",
      ...safeComp("fixed_costs", "fixed_costs"),
      realizedValue: sectionValue(actualSection, "fixed_costs"),
      forecastValue: sectionValue(forecastSection, "fixed_costs"),
      type: "currency",
      infoNode: <DashboardKpiInfoPopover kpiKey="charges_fixes_annuelles" />,
      dataTestId: "pilotage-dashboard-kpi-ceo-fixed-costs",
    },
    {
      label: forecastSection && !actualSection ? "Frais connus / prévus" : "Frais",
      ...safeComp("expenses", "expenses_annual_total"),
      actual: executive
        ? safeComp("expenses", "expenses_annual_total").actual
        : sameGlobalYear && canUseLegacyFallback ? finance?.expenses_annual_total ?? safeComp("expenses", "expenses_annual_total").actual : undefined,
      realizedValue: sectionValue(actualSection, "expenses"),
      forecastValue: sectionValue(forecastSection, "expenses"),
      type: "currency",
      valueClassName: "text-amber-300",
      subtext: "Frais SUBMITTED et APPROVED de la période",
      infoNode: <DashboardKpiInfoPopover kpiKey="frais_annuels" />,
      dataTestId: "pilotage-dashboard-kpi-ceo-expenses-annual",
    },
    {
      label: hasAssignedCount ? (forecastSection && !actualSection ? "Consultants en mission prévus" : "Consultants en mission") : forecastSection && !actualSection ? "Ressources prévues" : "Ressources",
      ...safeComp("active_resources", "resources_end_year"),
      ...(hasAssignedCount ? { target: null, delta: null, percent: null } : {}),
      subtext: hasAssignedCount ? `${staffing.total_resources} collaborateurs au total · ${staffing.intercontract_resources} en intercontrat` : undefined,
      actual: hasAssignedCount ? staffing.assigned_resources : executive
        ? safeComp("active_resources", "resources_end_year").actual
        : sameGlobalYear && canUseLegacyFallback ? activeResourcesCount ?? safeComp("active_resources", "resources_end_year").actual : undefined,
      realizedValue: hasAssignedCount ? actualSection?.operations?.assigned_resources : sectionValue(actualSection, "active_resources"),
      forecastValue: hasAssignedCount ? forecastSection?.operations?.assigned_resources : sectionValue(forecastSection, "active_resources"),
      type: "count",
      infoNode: <DashboardKpiInfoPopover kpiKey="consultants_actifs" />,
      dataTestId: "pilotage-dashboard-kpi-ceo-resources",
    },
    {
      label: "TJM moyen",
      ...safeComp("average_tjm", "tjm_net"),
      realizedValue: sectionValue(actualSection, "average_tjm"),
      forecastValue: sectionValue(forecastSection, "average_tjm"),
      type: "currency",
      infoNode: <DashboardKpiInfoPopover kpiKey="tjm_moyen" />,
      dataTestId: "pilotage-dashboard-kpi-ceo-tjm",
    },
    {
      label: forecastSection && !actualSection ? "ETP prévisionnels" : "ETP facturés",
      ...safeComp("billed_fte", "annual_billed_etp"),
      realizedValue: sectionValue(actualSection, "billed_fte"),
      forecastValue: sectionValue(forecastSection, "billed_fte"),
      type: "decimal",
      infoNode: <DashboardKpiInfoPopover kpiKey="etp_factures_annuels" />,
      dataTestId: "pilotage-dashboard-kpi-ceo-etp",
    },
    {
      label: forecastSection && !actualSection ? "Marge estimée" : "Marge %",
      ...safeComp("margin_pct", "margin_pct"),
      realizedValue: sectionValue(actualSection, "margin_pct"),
      forecastValue: sectionValue(forecastSection, "margin_pct"),
      type: "percent",
      infoNode: <DashboardKpiInfoPopover kpiKey="marge_brute_mission" />,
      dataTestId: "pilotage-dashboard-kpi-ceo-margin",
    },
  ].map((card) => ({ ...card, mixed: isMixed, showObjectives }));

  return (
    <DashboardSection
      title="PILOTAGE EXÉCUTIF"
      description={`Filtres propres à ce bloc uniquement. Indicateurs financiers et opérationnels sur la période sélectionnée. ${shownStart} → ${shownEnd}`}
      action={(
        <div className="flex w-full max-w-full flex-wrap items-center gap-2 sm:justify-end" data-testid="executive-period-controls">
          <select aria-label="Année du bloc exécutif" value={year} onChange={(event) => updateQuery({ executive_year: event.target.value })} className="h-11 max-w-full rounded-lg border border-white/10 bg-slate-900 px-3 text-sm text-white">
            {[2026, 2027].map((value) => <option key={value}>{value}</option>)}
          </select>
          <select aria-label="Période" value={preset} onChange={(event) => updateQuery({ period: event.target.value })} className="h-11 max-w-full rounded-lg border border-white/10 bg-slate-900 px-3 text-sm text-white">
            {PRESETS.map((value) => <option key={value} value={value}>{LABELS[value] || value}</option>)}
          </select>
          {preset === "CUSTOM" ? <>
            <input aria-label="Date début" type="date" value={customStart} onChange={(event) => updateQuery({ start: event.target.value })} className="h-11 max-w-full rounded-lg border border-white/10 bg-slate-900 px-3 text-sm text-white" />
            <span className="text-slate-500">→</span>
            <input aria-label="Date fin" type="date" value={customEnd} onChange={(event) => updateQuery({ end: event.target.value })} className="h-11 max-w-full rounded-lg border border-white/10 bg-slate-900 px-3 text-sm text-white" />
          </> : null}
          {loading ? <span className="text-xs text-slate-400">Actualisation…</span> : null}
        </div>
      )}
      className="glass"
      titleClassName="text-lenvia-blue text-xl font-bold uppercase tracking-wider"
    >
      {error ? <p className="mb-3 text-sm text-rose-300">{error}</p> : null}
      {actualSection && forecastSection ? <p className="mb-4 text-sm text-slate-300">Réalisé jusqu&apos;au {actualSection.end_date} <span className="text-slate-600">•</span> Estimatif du {forecastSection.start_date} au {forecastSection.end_date}</p> : null}
      {actualSection && !forecastSection ? <p className="mb-4 text-xs font-semibold text-emerald-300">✓ RÉALISÉ — {actualSection.start_date} → {actualSection.end_date}</p> : null}
      {forecastSection && !actualSection ? <p className="mb-4 text-xs font-semibold text-amber-300">◌ ESTIMATIF — {forecastSection.start_date} → {forecastSection.end_date}</p> : null}
      <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="executive-kpi-grid">
        {cards.map((card) => <ExecutiveCard key={card.dataTestId} {...card} />)}
      </div>
    </DashboardSection>
  );
};

export default CeoExecutiveKpis;
